"use client";
import { useEffect, useState } from "react";
import { Menu, Moon, Sun, X, LogOut } from "lucide-react";
import { Logo } from "../brand/logo";
import { Button } from "../ui/button";
import { OrgSelector } from "../OrgSelector";
import { cn } from "../../lib/cn";
import { useTheme } from "../../lib/theme";
import type { UserOrg } from "../../lib/org-context";

export interface NavItem {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  disabled?: boolean;
}

export interface NavGroup {
  label: string;
  items: NavItem[];
}

interface AppShellProps {
  user: { username?: string | null; email?: string | null; role: string };
  groups: NavGroup[];
  view: string;
  onNavigate: (id: string) => void;
  pageTitle: string;
  onSignOut: () => void;
  onOpenAccount: () => void;
  orgs: UserOrg[];
  currentOrg: UserOrg | null;
  onSelectOrg: (org: UserOrg) => void;
  /** Drop <main> padding so a child fills edge-to-edge (e.g. workflow editor). */
  fullBleed?: boolean;
  children: React.ReactNode;
}

export function AppShell({
  user, groups, view, onNavigate, pageTitle,
  onSignOut, onOpenAccount,
  orgs, currentOrg, onSelectOrg, fullBleed, children,
}: AppShellProps) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { theme, toggle } = useTheme();

  useEffect(() => {
    if (!mobileOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setMobileOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mobileOpen]);

  const go = (id: string) => { onNavigate(id); setMobileOpen(false); };
  const displayName = user.username || user.email || "user";
  const initial = displayName.charAt(0).toUpperCase();

  const Sidebar = (
    <div className="flex h-full flex-col bg-sidebar text-sidebar-foreground">
      <div className="flex h-14 items-center gap-3 border-b border-sidebar-border px-4">
        <Logo className="size-7 text-sidebar-foreground" />
        <div className="leading-tight">
          <strong className="block text-sm">nim-ai</strong>
          <span className="block text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
            PR Reviewer
          </span>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="ml-auto lg:hidden"
          aria-label="Close menu"
          onClick={() => setMobileOpen(false)}
        >
          <X className="size-5" />
        </Button>
      </div>

      <div className="border-b border-sidebar-border px-3 py-2.5">
        <OrgSelector orgs={orgs} current={currentOrg} onSelect={onSelectOrg} />
      </div>

      <nav className="flex-1 overflow-y-auto px-3 py-4" aria-label="Primary navigation">
        {groups.map((group) => (
          <div className="mb-5" key={group.label}>
            <div className="mb-1.5 flex items-center gap-2 px-2 text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
              {group.label}
            </div>
            {group.items.map((item) => {
              const active = !item.disabled && view === item.id;
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  onClick={() => { if (!item.disabled) go(item.id); }}
                  disabled={item.disabled}
                  aria-current={active ? "page" : undefined}
                  className={cn(
                    "mb-0.5 flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-sm transition-colors",
                    active
                      ? "bg-sidebar-accent font-medium text-sidebar-accent-foreground"
                      : "text-sidebar-foreground/75 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
                    item.disabled && "cursor-not-allowed opacity-55",
                  )}
                  title={item.disabled ? `${item.label} (coming soon)` : item.label}
                >
                  <Icon className="size-4 flex-shrink-0" />
                  <span className="truncate">{item.label}</span>
                  {item.disabled ? (
                    <span className="ml-auto text-[9px] font-medium uppercase tracking-wide text-muted-foreground">
                      soon
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>
        ))}
      </nav>

      <div className="border-t border-sidebar-border px-3 py-3">
        <div className="flex items-center gap-2.5">
          <button
            type="button"
            onClick={onOpenAccount}
            aria-label="Open account settings"
            className="flex min-w-0 flex-1 items-center gap-2.5 rounded-md px-1 py-1 text-left transition-colors hover:bg-sidebar-accent"
          >
            <span className="grid size-8 flex-shrink-0 place-items-center rounded-full bg-primary text-xs font-semibold text-primary-foreground">
              {initial}
            </span>
            <span className="min-w-0 flex-1 leading-tight">
              <strong className="block truncate text-xs">{displayName}</strong>
              <span className="block text-[10px] text-muted-foreground">{user.role}</span>
            </span>
          </button>
          <Button variant="ghost" size="icon" aria-label="Sign out" onClick={onSignOut}>
            <LogOut className="size-4" />
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-dvh bg-background text-foreground">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[100] focus:rounded-md focus:bg-primary focus:px-3 focus:py-2 focus:text-primary-foreground"
      >
        Skip to content
      </a>

      <aside className="fixed inset-y-0 left-0 z-40 hidden w-64 border-r border-sidebar-border bg-sidebar text-sidebar-foreground lg:flex lg:flex-col">
        {Sidebar}
      </aside>

      {mobileOpen ? (
        <div className="fixed inset-0 z-50 lg:hidden">
          <button
            type="button"
            aria-label="Close navigation"
            className="absolute inset-0 bg-black/45"
            onClick={() => setMobileOpen(false)}
          />
          <aside className="relative h-full w-[min(86vw,20rem)] border-r border-sidebar-border bg-sidebar shadow-xl">
            {Sidebar}
          </aside>
        </div>
      ) : null}

      <div className="lg:pl-64">
        <header className="sticky top-0 z-30 flex h-14 items-center gap-3 border-b border-border bg-background/90 px-4 backdrop-blur md:px-6">
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            aria-label="Open navigation"
            onClick={() => setMobileOpen(true)}
          >
            <Menu className="size-5" />
          </Button>
          <h1 className="text-sm font-semibold">{pageTitle}</h1>
          <div className="ml-auto flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              aria-label={theme === "dark" ? "Switch to light theme" : "Switch to dark theme"}
              onClick={toggle}
            >
              {theme === "dark" ? <Sun className="size-4" /> : <Moon className="size-4" />}
            </Button>
          </div>
        </header>

        <main id="main-content" className={cn("min-h-[calc(100dvh-3.5rem)]", fullBleed ? "" : "p-4 md:p-6")}>
          {children}
        </main>
      </div>
    </div>
  );
}